import React from 'react';
import PropTypes from 'prop-types';

import gettext from '@eb/gettext';
import { getAPIDateTimeFormats } from '@eb/datetime';
import DateThumbnail from 'eventbrite_design_system/dateThumbnail/DateThumbnail';
import EditListItem from 'eventbrite_design_system/editListItem/EditListItem';
import Icon from 'eventbrite_design_system/icon/Icon';

import StatusDotChunkySvg from 'eventbrite_design_system/iconography/icons/StatusDotChunky';
import TicketMultiSvg from 'eventbrite_design_system/iconography/icons/TicketMulti';

import {
    EVENT_IMAGE_PLACEHOLDER_URL,
    EVENT_SALES_STATUS_ICON_COLOR_MAP,
    EVENT_STATUS_LABEL,
    MAP_CONFLICTS_TO_TRANSLATION,
} from '../../constants/downgrade';

import './eventsConflictsTableBody.scss';

const StatusLabel = ({ status }) => {
    let icon;
    const iconInfo = EVENT_SALES_STATUS_ICON_COLOR_MAP[status];

    if (iconInfo) {
        icon = <Icon type={<StatusDotChunkySvg />} color={iconInfo} />;
    }

    return (
        <div
            className="sales-status eds-l-pad-right-2"
            data-spec="conflicts-table-body-status-label"
        >
            <span className="eds-text-color--grey-900">
                {icon}
                {EVENT_STATUS_LABEL[status] || EVENT_STATUS_LABEL.default}
            </span>
        </div>
    );
};

const TimeAndVenue = ({ event }) => {
    const { fullDate, fullDateWeekday, time } = getAPIDateTimeFormats(
        event.start,
    );
    // Translators: This is a date and time together. For example: "August 27, 2019 at 8:00 PM PST"
    const smallScreenDateTime = gettext('%(date)s at %(time)s', {
        date: fullDate,
        time,
    });
    // Translators: This is a date and time together. For example: "Wednesday, August 27, 2019 at 8:00 PM PST"
    const largeScreenDateTime = gettext('%(date)s at %(time)s', {
        date: fullDateWeekday,
        time,
    });

    return (
        <div
            className="eds-l-mar-vert-1"
            data-spec="conflicts-table-body-time-and-venue"
        >
            <p className="eds-show-down-ln">{smallScreenDateTime}</p>
            <p className="eds-show-up-lg">{largeScreenDateTime}</p>
        </div>
    );
};

const DateSquare = ({ event }) => (
    <div className="eds-show-up-lg" data-spec="conflicts-table-body-date">
        <DateThumbnail date={event.start.local} />
    </div>
);

const SeriesDisplay = () => (
    <p data-spec="conflicts-table-body-series">
        <Icon
            color="ui-900"
            type={
                <TicketMultiSvg
                    size="xsmall"
                    title={gettext('Multiple Events')}
                />
            }
        />
        <span className="l-mar-left-1 eds-text-color--ui-900">
            {gettext('Multiple Events')}
        </span>
    </p>
);

const TertiaryContent = ({ event: { status }, isSeries }) => {
    let seriesDisplay;

    if (isSeries) {
        seriesDisplay = <SeriesDisplay />;
    }

    return (
        <div>
            {seriesDisplay}
            <StatusLabel status={status} />
        </div>
    );
};

const EventsConflictsTableBody = ({ conflicts }) => {
    const items = conflicts.map(({ event, downgradeConflicts }) => {
        const {
            id,
            name: { text: eventName },
            logo,
            isSeries,
        } = event;
        const logoUrl = logo ? logo.url : EVENT_IMAGE_PLACEHOLDER_URL;

        return (
            <EditListItem
                data-spec={`conflicts-table-row-${id}`}
                __containerClassName="conflict-event-list__item"
                key={id}
                content={eventName}
                secondaryContent={<TimeAndVenue event={event} />}
                tertiaryContent={
                    <TertiaryContent event={event} isSeries={isSeries} />
                }
                imageUrl={logoUrl}
                extraContent={<DateSquare event={event} />}
                isSquareImage={true}
                actionItems={[]}
                noWrap={true}
            >
                <div className="eds-g-cell-1-1 eds-g-cell-lg-7-12 eds-g-cell-ln-8-12 eds-g-cell-lw-7-12 eds-g-cell-md-9-12 eds-g-cell-mw-8-12 eds-g-group eds-text-color--grey-600">
                    <div className="eds-l-pad-hor-2 eds-text--left">
                        <ul>
                            {downgradeConflicts.map((conflict) => (
                                <li key={conflict}>
                                    <span className="eds-text-bm">
                                        {MAP_CONFLICTS_TO_TRANSLATION[conflict]}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </EditListItem>
        );
    });

    return (
        <div className="conflict-event-list" data-spec="conflicts-table-body">
            {items}
        </div>
    );
};

EventsConflictsTableBody.propTypes = {
    conflicts: PropTypes.arrayOf(
        PropTypes.shape({
            event: PropTypes.objectOf(PropTypes.any),
            downgradeConflicts: PropTypes.arrayOf(PropTypes.string),
        }),
    ),
};

export default EventsConflictsTableBody;
